import classNames from 'classnames';
import { memo } from 'react';

import { type AirActionTrackingLocation } from '@air/analytics';
import { ArrowDownIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';
import { DropdownMenu, renderDropdownItems } from '@air/primitive-dropdown-menu';

import { useCopyToWorkspace } from '~/components/CopyToWorkspace/hooks/useCopyToWorkspace';
import { getBrandedButtonStyle } from '~/components/v2/utils/filters/getContrastColor';
import { useDownloadPublicBoard } from '~/hooks/useDownloadPublicBoard';
import { usePublicCustomColors } from '~/hooks/usePublicCustomColors';
import { type BoardPageBoard } from '~/store/centralizedBoard/types';
import { getDownloadMenuOptions } from '~/utils/menuOptions/getDownloadMenuOptions';

export type PublicBoardDownloadMenuProps = {
  board: BoardPageBoard;
  className?: string;
  size?: 'small' | 'medium';
  trackLocation?: AirActionTrackingLocation;
};

export const PublicBoardDownloadMenu = memo(
  ({ board, className, size, trackLocation = 'header' }: PublicBoardDownloadMenuProps) => {
    const { downloadPublicBoard } = useDownloadPublicBoard();
    const { copyToAir } = useCopyToWorkspace();
    const { buttonBgColor } = usePublicCustomColors();

    // Preserve the original trigger size ('large') when no override is provided.
    const buttonSize = size ?? 'large';
    const prefixIconSize = buttonSize === 'small' ? 'xs' : 'sm';

    const menuOptions = getDownloadMenuOptions({
      onDownload: () =>
        downloadPublicBoard({
          boardId: board.id,
          trackLocation,
        }),
      onSaveToAir: () =>
        copyToAir({
          boardId: board.id,
          trackLocation,
        }),
    });

    return (
      <DropdownMenu
        trigger={
          <Button
            appearance="filled"
            color="blue"
            size={buttonSize}
            className={classNames('ml-2', className)}
            prefix={<ArrowDownIcon size={prefixIconSize} />}
            data-testid="PUBLIC_BOARD_DOWNLOAD_MENU_TRIGGER"
            style={buttonBgColor ? getBrandedButtonStyle(buttonBgColor) : undefined}
          >
            Save all
          </Button>
        }
      >
        {renderDropdownItems({ options: menuOptions })}
      </DropdownMenu>
    );
  },
);

PublicBoardDownloadMenu.displayName = 'PublicBoardDownloadMenu';
